import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessPlan } from '../../App';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  ArrowLeft,
  Save,
  Eye,
  Settings,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import BlockPalette from './BlockPalette';
import SortableBlock from './SortableBlock';
import BlockConfigurator from './BlockConfigurator';
import TemplateSettingsModal from './TemplateSettingsModal';
import ExportButton from './ExportButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Droppable zone component for empty state
const DroppableZone = ({ id, children, isEmpty }) => {
  const { isOver, setNodeRef } = useDroppable({ id });
  
  return (
    <div
      ref={setNodeRef}
      className={`
        border-2 border-dashed rounded-lg transition-colors
        ${isOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
        ${isEmpty ? 'p-8 text-center' : 'p-4 mt-4 text-center text-sm text-gray-400'}
      `}
    >
      {children}
    </div>
  );
};

// Default template structure
const DEFAULT_TEMPLATE = {
  name: 'Untitled Template',
  description: '',
  audience: 'custom',
  branding: {
    logo_url: null,
    logo_position: 'center',
    theme: 'professional',
    primary_color: '#1f2937',
    secondary_color: '#3b82f6',
    accent_color: '#10b981',
    font_heading: 'Inter',
    font_body: 'Inter'
  },
  cover_page: {
    title: 'Business Plan',
    subtitle: '{{company_name}}',
    show_logo: true,
    logo_position: 'center',
    show_date: true,
    show_prepared_for: false,
    prepared_for_label: 'Prepared for:',
    prepared_for_value: ''
  },
  blocks: [],
  options: {
    page_size: 'A4',
    orientation: 'portrait',
    include_page_numbers: true,
    page_number_position: 'bottom-center',
    include_header: false,
    header_text: '',
    include_footer: false,
    footer_text: '',
    margins: { top: 25, right: 20, bottom: 25, left: 20 }
  },
  sharing: {
    visibility: 'private',
    shared_with: [],
    is_preset: false,
    category: null
  }
};

const TemplateBuilder = () => {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { currentPlan } = useBusinessPlan();
  const preset = searchParams.get('preset');

  // State
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved', 'error'
  const [activeBlock, setActiveBlock] = useState(null);
  const [configBlock, setConfigBlock] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [dataSources, setDataSources] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Fetch template or create new
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // Fetch data sources
        const sourcesRes = await fetch(`${BACKEND_URL}/api/report-templates/sources`);
        const sourcesData = await sourcesRes.json();
        setDataSources(sourcesData);

        if (templateId) {
          // Load existing template
          const res = await fetch(`${BACKEND_URL}/api/report-templates/${templateId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setTemplate(data.template);
          }
        } else if (preset) {
          // Load preset configuration
          const presetConfig = getPresetConfig(preset);
          setTemplate({ ...DEFAULT_TEMPLATE, ...presetConfig });
        }
      } catch (error) {
        console.error('Error initializing builder:', error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [templateId, preset, token]);

  // Get preset configuration
  const getPresetConfig = (presetType) => {
    const presets = {
      bank: {
        name: 'Bank Loan Application',
        description: 'Complete loan application package for banks',
        audience: 'bank',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'toc', type: 'table_of_contents', order: 1, title: 'Table of Contents', config: {} },
          { id: 'exec', type: 'narrative', order: 2, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'standard', focus_areas: ['financial_highlights', 'loan_purpose'] } } },
          { id: 'income', type: 'data_section', order: 3, title: 'Income Statement', config: { source: 'outputs.income_statement', detail_level: 'detailed' } },
          { id: 'balance', type: 'data_section', order: 4, title: 'Balance Sheet', config: { source: 'outputs.balance_sheet', detail_level: 'standard' } },
          { id: 'cashflow', type: 'data_section', order: 5, title: 'Cash Flow', config: { source: 'outputs.cash_flow', detail_level: 'standard' } },
          { id: 'revenue_chart', type: 'chart', order: 6, title: 'Revenue Projections', config: { source: 'chart.revenue_trend', chart_type: 'bar' } },
          { id: 'loan_util', type: 'custom_table', order: 7, title: 'Loan Utilization Plan', config: { columns: [{ key: 'purpose', label: 'Purpose' }, { key: 'amount', label: 'Amount' }, { key: 'timeline', label: 'Timeline' }], rows: [] } },
          { id: 'collateral', type: 'narrative', order: 8, title: 'Collateral Offered', config: { source: 'custom_text', custom_text: '' } }
        ]
      },
      investor: {
        name: 'Investor Pitch Deck',
        description: 'Compelling presentation for investors',
        audience: 'investor',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'exec', type: 'narrative', order: 1, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'brief', focus_areas: ['growth_potential', 'market_position'] } } },
          { id: 'market', type: 'narrative', order: 2, title: 'Market Opportunity', config: { source: 'ai_generated', ai_settings: { tone: 'formal', focus_areas: ['market_size', 'trends'] } } },
          { id: 'revenue', type: 'data_section', order: 3, title: 'Revenue Projections', config: { source: 'inputs.sales_forecasts', detail_level: 'summary' } },
          { id: 'growth_chart', type: 'chart', order: 4, title: 'Growth Trajectory', config: { source: 'chart.revenue_trend', chart_type: 'line' } },
          { id: 'financials', type: 'data_section', order: 5, title: 'Financial Summary', config: { source: 'outputs.income_statement', detail_level: 'summary' } }
        ]
      },
      government: {
        name: 'Government Submission',
        description: 'Regulatory compliance business plan',
        audience: 'government',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'toc', type: 'table_of_contents', order: 1, title: 'Table of Contents', config: {} },
          { id: 'exec', type: 'narrative', order: 2, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'standard' } } },
          { id: 'company', type: 'data_section', order: 3, title: 'Company Overview', config: { source: 'company.profile', detail_level: 'detailed' } },
          { id: 'employment', type: 'custom_table', order: 4, title: 'Employment Plan', config: { columns: [{ key: 'position', label: 'Position' }, { key: 'count', label: 'Number' }, { key: 'timeline', label: 'Hiring Timeline' }], rows: [] } },
          { id: 'income', type: 'data_section', order: 5, title: 'Financial Projections', config: { source: 'outputs.income_statement', detail_level: 'detailed' } },
          { id: 'tax', type: 'narrative', order: 6, title: 'Tax Compliance', config: { source: 'custom_text', custom_text: '' } }
        ]
      },
      donor: {
        name: 'Donor Impact Report',
        description: 'Impact-focused report for donors and NGOs',
        audience: 'donor',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'exec', type: 'narrative', order: 1, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'standard', focus_areas: ['financial_highlights'] } } },
          { id: 'impact', type: 'narrative', order: 2, title: 'Impact Summary', config: { source: 'custom_text', custom_text: '' } },
          { id: 'budget', type: 'data_section', order: 3, title: 'Budget Overview', config: { source: 'inputs.planned_costs', detail_level: 'standard' } },
          { id: 'outcomes', type: 'custom_table', order: 4, title: 'Expected Outcomes', config: { columns: [{ key: 'outcome', label: 'Outcome' }, { key: 'indicator', label: 'Indicator' }, { key: 'target', label: 'Target' }], rows: [] } },
          { id: 'sustainability', type: 'narrative', order: 5, title: 'Sustainability Plan', config: { source: 'custom_text', custom_text: '' } }
        ]
      },
      board: {
        name: 'Board Quarterly Report',
        description: 'Executive summary for board meetings',
        audience: 'board',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'exec', type: 'narrative', order: 1, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'brief' } } },
          { id: 'kpis', type: 'data_section', order: 2, title: 'Key Performance Indicators', config: { source: 'outputs.financial_ratios', detail_level: 'summary' } },
          { id: 'revenue', type: 'chart', order: 3, title: 'Revenue Performance', config: { source: 'chart.revenue_trend', chart_type: 'bar' } },
          { id: 'profitability', type: 'chart', order: 4, title: 'Profitability Trend', config: { source: 'chart.profit_margin', chart_type: 'line' } },
          { id: 'strategic', type: 'data_section', order: 5, title: 'Strategic Initiatives', config: { source: 'inputs.strategic_initiatives', detail_level: 'summary' } }
        ]
      },
      internal: {
        name: 'Internal Planning Document',
        description: 'Comprehensive internal business plan',
        audience: 'internal',
        blocks: [
          { id: 'cover', type: 'cover_page', order: 0, title: 'Cover Page', config: {} },
          { id: 'toc', type: 'table_of_contents', order: 1, title: 'Table of Contents', config: {} },
          { id: 'exec', type: 'narrative', order: 2, title: 'Executive Summary', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'detailed' } } },
          { id: 'strategic', type: 'data_section', order: 3, title: 'Strategic Framework', config: { source: 'inputs.strategic_objectives', detail_level: 'detailed' } },
          { id: 'products', type: 'data_section', order: 4, title: 'Products & Services', config: { source: 'inputs.products', detail_level: 'detailed' } },
          { id: 'income', type: 'data_section', order: 5, title: 'Income Statement', config: { source: 'outputs.income_statement', detail_level: 'detailed' } },
          { id: 'balance', type: 'data_section', order: 6, title: 'Balance Sheet', config: { source: 'outputs.balance_sheet', detail_level: 'detailed' } },
          { id: 'cashflow', type: 'data_section', order: 7, title: 'Cash Flow', config: { source: 'outputs.cash_flow', detail_level: 'detailed' } },
          { id: 'costs', type: 'data_section', order: 8, title: 'Cost Analysis', config: { source: 'inputs.planned_costs', detail_level: 'detailed' } }
        ]
      }
    };
    return presets[presetType] || {};
  };

  // Save template
  const saveTemplate = useCallback(async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const method = template.id ? 'PUT' : 'POST';
      const url = template.id 
        ? `${BACKEND_URL}/api/report-templates/${template.id}`
        : `${BACKEND_URL}/api/report-templates`;
      
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(template)
      });
      
      if (res.ok) {
        const data = await res.json();
        setTemplate(data.template);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
        
        // If new template, update URL
        if (!template.id && data.template.id) {
          navigate(`/reports/builder/${data.template.id}`, { replace: true });
        }
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [template, token, navigate]);

  // Auto-save on changes (debounced)
  useEffect(() => {
    if (!loading && template.id) {
      const timer = setTimeout(() => {
        saveTemplate();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [template.blocks, template.name, template.description]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveBlock(null);
    
    if (!over) return;
    
    // Check if dropping from palette (new block)
    if (active.id.startsWith('palette-')) {
      const blockType = active.id.replace('palette-', '');
      const newBlock = createNewBlock(blockType);
      
      // Find insert position
      let insertIndex = template.blocks.length;
      
      if (over.id === 'drop-zone' || over.id === 'empty-drop-zone') {
        // Dropping in drop zone - add at end
        insertIndex = template.blocks.length;
      } else {
        // Dropping on existing block
        const overIndex = template.blocks.findIndex(b => b.id === over.id);
        insertIndex = overIndex >= 0 ? overIndex : template.blocks.length;
      }
      
      const newBlocks = [...template.blocks];
      newBlocks.splice(insertIndex, 0, newBlock);
      
      // Update order
      newBlocks.forEach((b, i) => b.order = i);
      setTemplate({ ...template, blocks: newBlocks });
      
      // Open configurator for new block
      setTimeout(() => setConfigBlock(newBlock), 100);
    } else if (active.id !== over.id && over.id !== 'drop-zone' && over.id !== 'empty-drop-zone') {
      // Reordering existing blocks
      const oldIndex = template.blocks.findIndex(b => b.id === active.id);
      const newIndex = template.blocks.findIndex(b => b.id === over.id);
      
      if (oldIndex >= 0 && newIndex >= 0) {
        const newBlocks = arrayMove(template.blocks, oldIndex, newIndex);
        newBlocks.forEach((b, i) => b.order = i);
        setTemplate({ ...template, blocks: newBlocks });
      }
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    if (active.id.startsWith('palette-')) {
      setActiveBlock({ type: active.id.replace('palette-', ''), isNew: true });
    } else {
      const block = template.blocks.find(b => b.id === active.id);
      setActiveBlock(block);
    }
  };

  // Create new block
  const createNewBlock = (type) => {
    const id = `block-${Date.now()}`;
    const blockDefaults = {
      // Document Structure
      cover_page: { title: 'Cover Page', config: {} },
      table_of_contents: { title: 'Table of Contents', config: { depth: 2 } },
      page_break: { title: 'Page Break', config: {} },
      
      // Content Blocks
      narrative: { title: 'Narrative Section', config: { source: 'ai_generated', ai_settings: { tone: 'formal', length: 'standard', language: 'en', focus_areas: [] } } },
      data_section: { title: 'Data Section', config: { source: 'outputs.income_statement', detail_level: 'standard', years: [2025, 2026, 2027], include_chart: false } },
      chart: { title: 'Chart', config: { source: 'chart.revenue_trend', chart_type: 'bar', show_legend: true } },
      custom_table: { title: 'Custom Table', config: { columns: [], rows: [], show_totals: false, editable: true } },
      
      // Business Plan Input Sections
      section_strategic_framework: { 
        title: 'Strategic Framework', 
        config: { 
          section_type: 'strategic_framework',
          selected_tabs: ['objectives', 'initiatives', 'products', 'sales_forecasts'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_cost_centers: { 
        title: 'Cost Centers', 
        config: { 
          section_type: 'cost_centers',
          selected_tabs: ['centers', 'allocations'],
          include_ai_summary: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_cost_management: { 
        title: 'Cost Management', 
        config: { 
          section_type: 'cost_management',
          selected_tabs: ['core_costs', 'operations', 'investments', 'financial'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_financial_management: { 
        title: 'Financial Management', 
        config: { 
          section_type: 'financial_management',
          selected_tabs: ['current_debt', 'current_equity', 'capital_structure'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_financial_planning: { 
        title: 'Financial Planning', 
        config: { 
          section_type: 'financial_planning',
          selected_tabs: ['overview', 'planned_debt', 'planned_equity', 'milestones'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      
      // Financial Output Sections
      section_financial_statements: { 
        title: 'Financial Statements', 
        config: { 
          section_type: 'financial_statements',
          selected_tabs: ['income_statement', 'balance_sheet', 'cash_flow', 'cost_analysis'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_financial_analytics: { 
        title: 'Financial Analytics', 
        config: { 
          section_type: 'financial_analytics',
          selected_tabs: ['financial', 'breakeven', 'roi', 'cashflow'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_planning_dashboard: { 
        title: 'Planning Dashboard', 
        config: { 
          section_type: 'planning_dashboard',
          selected_tabs: ['kpis', 'charts', 'summaries'],
          include_ai_summary: true,
          include_charts: true,
          include_kpis: true,
          detail_level: 'standard'
        } 
      },
      section_scenario_modeling: { 
        title: 'Scenario Modeling', 
        config: { 
          section_type: 'scenario_modeling',
          selected_tabs: ['scenarios', 'comparisons'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      
      // Performance Sections
      section_executive_dashboard: { 
        title: 'Executive Dashboard', 
        config: { 
          section_type: 'executive_dashboard',
          selected_tabs: ['overview', 'highlights'],
          include_ai_summary: true,
          include_charts: true,
          include_kpis: true,
          detail_level: 'standard'
        } 
      },
      section_revenue_analysis: { 
        title: 'Revenue Analysis', 
        config: { 
          section_type: 'revenue_analysis',
          selected_tabs: ['actuals', 'by_product', 'comparisons'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_cost_analysis: { 
        title: 'Cost Analysis', 
        config: { 
          section_type: 'cost_analysis_performance',
          selected_tabs: ['actuals', 'by_category', 'trends'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_profitability: { 
        title: 'Profitability Analysis', 
        config: { 
          section_type: 'profitability',
          selected_tabs: ['margins', 'pl_comparison'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_variance: { 
        title: 'Variance Analytics', 
        config: { 
          section_type: 'variance',
          selected_tabs: ['revenue', 'cost', 'profit'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      section_trends: { 
        title: 'Trends & Forecasting', 
        config: { 
          section_type: 'trends',
          selected_tabs: ['revenue_trends', 'cost_trends', 'forecasts'],
          include_ai_summary: true,
          include_charts: true,
          include_tables: true,
          detail_level: 'standard'
        } 
      },
      
      // Company Section
      section_company_profile: { 
        title: 'Company Profile', 
        config: { 
          section_type: 'company',
          selected_tabs: ['details', 'team'],
          include_ai_summary: true,
          detail_level: 'standard'
        } 
      }
    };
    
    return {
      id,
      type,
      order: template.blocks.length,
      ...blockDefaults[type]
    };
  };

  // Delete block
  const handleDeleteBlock = (blockId) => {
    const newBlocks = template.blocks.filter(b => b.id !== blockId);
    newBlocks.forEach((b, i) => b.order = i);
    setTemplate({ ...template, blocks: newBlocks });
  };

  // Move block up or down
  const handleMoveBlock = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= template.blocks.length) return;
    
    const newBlocks = arrayMove(template.blocks, fromIndex, toIndex);
    newBlocks.forEach((b, i) => b.order = i);
    setTemplate({ ...template, blocks: newBlocks });
  };

  // Update block
  const handleUpdateBlock = (blockId, updates) => {
    const newBlocks = template.blocks.map(b => 
      b.id === blockId ? { ...b, ...updates } : b
    );
    setTemplate({ ...template, blocks: newBlocks });
    setConfigBlock(null);
  };

  // Estimate pages
  const estimatePages = () => {
    let pages = 0;
    template.blocks.forEach(block => {
      switch (block.type) {
        case 'cover_page': pages += 1; break;
        case 'table_of_contents': pages += 1; break;
        case 'narrative': pages += block.config?.ai_settings?.length === 'detailed' ? 2 : 1; break;
        case 'data_section': pages += block.config?.detail_level === 'detailed' ? 2 : 1; break;
        case 'chart': pages += 1; break;
        case 'custom_table': pages += 1; break;
        case 'page_break': pages += 0; break;
        // Section blocks estimate based on selected tabs and detail level
        case 'section_strategic_framework':
        case 'section_cost_centers':
        case 'section_cost_management':
        case 'section_financial_management':
        case 'section_financial_planning':
        case 'section_financial_statements':
        case 'section_financial_analytics':
        case 'section_planning_dashboard':
        case 'section_scenario_modeling':
        case 'section_executive_dashboard':
        case 'section_revenue_analysis':
        case 'section_cost_analysis':
        case 'section_profitability':
        case 'section_variance':
        case 'section_trends':
        case 'section_company_profile': {
          const tabCount = block.config?.selected_tabs?.length || 1;
          const detailMultiplier = block.config?.detail_level === 'detailed' ? 2 : 1;
          pages += Math.ceil(tabCount * detailMultiplier);
          break;
        }
        default: pages += 1;
      }
    });
    return Math.max(1, pages);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <Input
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
                  placeholder="Template Name"
                />
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">
                    {template.audience}
                  </Badge>
                  <span className="text-xs text-gray-400">
                    {template.blocks.length} blocks • ~{estimatePages()} pages
                  </span>
                  {currentPlan ? (
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                      Plan: {currentPlan.name}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                      No plan selected
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {saveStatus === 'saved' && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" /> Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" /> Error saving
                </span>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/reports/preview/${template.id}`)} disabled={!template.id}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button size="sm" onClick={saveTemplate} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
              {template.id && (
                <ExportButton 
                  templateId={template.id} 
                  templateName={template.name}
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Builder */}
      <div className="max-w-7xl mx-auto p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6">
            {/* Block Palette */}
            <div className="w-64 flex-shrink-0">
              <BlockPalette dataSources={dataSources} />
            </div>

            {/* Report Structure */}
            <div className="flex-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Report Structure
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {template.blocks.length === 0 ? (
                    <DroppableZone id="empty-drop-zone" isEmpty={true}>
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500 mb-1">No blocks added yet</p>
                      <p className="text-sm text-gray-400">
                        Drag blocks from the palette to build your report
                      </p>
                    </DroppableZone>
                  ) : (
                    <SortableContext
                      items={template.blocks.map(b => b.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {template.blocks.map((block, index) => (
                          <SortableBlock
                            key={block.id}
                            block={block}
                            index={index}
                            totalBlocks={template.blocks.length}
                            onConfigure={() => setConfigBlock(block)}
                            onDelete={() => handleDeleteBlock(block.id)}
                            onMoveUp={() => handleMoveBlock(index, index - 1)}
                            onMoveDown={() => handleMoveBlock(index, index + 1)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  )}
                  
                  {/* Drop zone at bottom - always visible when there are blocks */}
                  {template.blocks.length > 0 && (
                    <DroppableZone id="drop-zone" isEmpty={false}>
                      <Plus className="h-4 w-4 inline mr-1" />
                      Drop blocks here to add to report
                    </DroppableZone>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeBlock && (
              <div className="bg-white shadow-lg rounded-lg p-3 border-2 border-blue-500 opacity-90">
                <span className="font-medium">{activeBlock.title || activeBlock.type}</span>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Block Configurator Modal */}
      {configBlock && (
        <BlockConfigurator
          block={configBlock}
          dataSources={dataSources}
          planId={currentPlan?.id}
          onSave={(updates) => handleUpdateBlock(configBlock.id, updates)}
          onClose={() => setConfigBlock(null)}
        />
      )}

      {/* Template Settings Modal */}
      {showSettings && (
        <TemplateSettingsModal
          template={template}
          onSave={(updates) => {
            setTemplate({ ...template, ...updates });
            setShowSettings(false);
          }}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default TemplateBuilder;
